'use client';

import { useRouter } from 'next/navigation';
import { FC, useCallback, useMemo, useState } from 'react';
import { css } from '@bts-web/utils-style-engine';
import { isDevelopmentEnv } from '@bts-web/utils-functions';
import { useClientLocale, useClientTranslation } from '@bts-web/utils-lokalise';
import { AmountFor } from '@bts-web/data-layer/server';
import { DatadogErrorLevels } from '@bts-web/utils-next-infrastructure';
import { getAssetTradeUrl } from '../../../route-builders';
import {
  PrecisionFloat,
  capitalizeFirstLetter,
  getPrecisionFloatValue,
} from '@bts-web/utils-formatting';
import { isBpfsAsset } from '../../utils/isBpfsAsset';
import {
  AssetType,
  submitButtonWrapperAndButtonProperStyles,
} from '../../utils';
import { OfferLoaderElement } from '../../Reusable/OfferLoaderElement/OfferLoaderElement';
import { PoweredBy } from '../../Reusable/PoweredBy/PoweredBy';
import { SubmitButtonWrapper } from '../../Reusable/SubmitButtonWrapper/SubmitButtonWrapper';
import { TradeTaxSummary } from '../../Reusable/TradeTaxSummary/TradeTaxSummary';
import { AcceptBuyTradeDisclaimerWrapper } from './components/AcceptBuyTradeDisclaimerWrapper/AcceptBuyTradeDisclaimerWrapper';
import {
  finishTradeRedirectBuilder,
  IFinishTradeRedirectBuilderProps,
} from '../../FinishTrade/actions/finishTradeRedirectBuilder.action';
import { TradeAssetInfoDisplay } from '../../../common/components/TradeAssetInfoDisplay/TradeAssetInfoDisplay';
import { TradeScreenTranslations } from '../../utils/getTradeTranslations';
import {
  AcceptTradeControllerVariants,
  TradeOfferDataNonNullable,
} from '../types';
import {
  AmountInputWrapper,
  ButtonBase,
  Divider,
  useHandleGqlErrorsWithSnackbar,
  datadogErrorHelper,
  AlertBox,
} from '../../../common';
import {
  acceptTradeOffer,
  getTradeOfferData,
} from '../../../common/gqlActions';
import { BelowInputInfoTable } from '../../../common/components/BelowInputInfoTable/BelowInputInfoTable';
import { getConvertedSummary } from '../../CreateTrade/utils/getConvertedSummary';
import { removeInputValueFromLocalStorage } from '../../CreateTrade/utils/localStorageInputValueUtils';
import { useAppNotification } from '../../../notifications/NotificationContextProvider';
import { getMaximumTradeAmount } from '../../CreateTrade/utils';
import { FeesList } from '../../Reusable/FeesList/FeesList';
import { translateOfferWarnings } from '../../utils/translateOfferWarnings';
import { getFeesListItemsForTrade } from './getFeesListItemsForTrade';
import { getAppConfig } from '@bts-web/core-app-config';
import { useFeatureFlag } from '../../../statsig/sdk/provider';
import { FilledSpinnerIos } from '../../../common/svgIcons';
import { MutableNonNullableAsset } from '../../CreateTrade/types';
import { MINIMUM_REFRESH_TIME } from '../../utils/getOfferAvailabilityInMs';

export type AcceptTradeSubscriptionWrapperProps = {
  initialOfferData: TradeOfferDataNonNullable;
  offerId: string;
  translations: TradeScreenTranslations;
  variant: AcceptTradeControllerVariants;
};

function isNextRedirectError(error: unknown): boolean {
  if (error instanceof Error) {
    if (error.message === 'NEXT_REDIRECT') return true;

    const digest = (error as Error & { digest?: string }).digest;

    if (digest?.includes('NEXT_REDIRECT')) return true;
  }

  return false;
}

export const AcceptTradeSubscriptionWrapper: FC<
  AcceptTradeSubscriptionWrapperProps
> = ({ initialOfferData, offerId, translations, variant }) => {
  const { currency } = getAppConfig();

  const feature_areTaxDetailsShownOnOfferAccept = useFeatureFlag(
    'feature_areTaxDetailsShownOnOfferAccept',
  );

  const feature_isPaymentMethodVisibleOnAccept = useFeatureFlag(
    'feature_isPaymentMethodVisibleOnAccept',
  );

  const { t } = useClientTranslation();

  const currentLocale = useClientLocale();

  const [offerWarnings, setOfferWarnings] = useState<string[]>(() => {
    const warnings: string[] =
      initialOfferData?.warnings
        ?.map((item) => item.messageKey)
        .filter((item) => typeof item === 'string') ?? [];

    return translateOfferWarnings(warnings, t);
  });

  const { setAppNotification } = useAppNotification();

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const router = useRouter();

  const [tradeOfferData, setTradeOfferData] =
    useState<TradeOfferDataNonNullable>(initialOfferData);

  const [nextExpiresAtInMs, setNextExpiresAtInMs] = useState<number | null>(
    initialOfferData.expiresAtInSeconds > 0
      ? initialOfferData.expiresAtInSeconds * 1000
      : null,
  );

  const [isTradeOfferViewQueryRefetching, setIsTradeOfferViewQueryRefetching] =
    useState(false);

  const [isOfferAccepted, setOfferAccepted] = useState(false);

  // Bumped on every successful refetch so the loader remounts and restarts its
  // countdown, even though expiresAtInSeconds is a (near-)constant duration.
  const [refreshCount, setRefreshCount] = useState(0);

  const offerAvailabilityInMs = nextExpiresAtInMs ?? MINIMUM_REFRESH_TIME;

  const refetchTradeOfferViewQuery = useCallback(async () => {
    if (isTradeOfferViewQueryRefetching || isOfferAccepted) return;

    setIsTradeOfferViewQueryRefetching(true);

    try {
      const response = await getTradeOfferData(offerId);

      const tradeOffer = response.data?.tradeOffer;

      if (response.errors) {
        processErrors(response.errors);

        setNextExpiresAtInMs(null);

        return;
      }

      if (!tradeOffer) {
        console.warn('%cNo trade offer data found', 'color:red');

        if (!isDevelopmentEnv()) {
          router.push('/error/server');
        }
      } else {
        setTradeOfferData(tradeOffer);

        const recievedWarnings: string[] =
          tradeOffer?.warnings
            ?.map((item) => item.messageKey)
            .filter((item) => typeof item === 'string') ?? [];

        setOfferWarnings(translateOfferWarnings(recievedWarnings, t));

        setNextExpiresAtInMs(
          tradeOffer.expiresAtInSeconds > 0
            ? tradeOffer.expiresAtInSeconds * 1000
            : null,
        );

        setRefreshCount((c) => c + 1);

        document.dispatchEvent(new Event('onTradeOfferRefetched'));
      }
    } catch (error) {
      console.error('Error fetching trade offer:', error);

      if (typeof error === 'string') {
        processErrors([error] as string[]);
      } else {
        processErrors(error as unknown as string[]);
      }

      setNextExpiresAtInMs(null);
    } finally {
      setIsTradeOfferViewQueryRefetching(false);
    }
  }, [
    offerId,
    isOfferAccepted,
    isDevelopmentEnv,
    router,
    setAppNotification,
    translations,
  ]);

  const handleSpinnerComplete = useCallback(() => {
    refetchTradeOfferViewQuery();
  }, [refetchTradeOfferViewQuery]);

  const { asset, totalAmount, taxAmount, fiatAmount, hasTaxOfflineWarning } =
    tradeOfferData;

  const { symbol: initialSymbol, __typename } = asset;

  const symbol =
    __typename === 'MetalAsset' ? translations.grams : initialSymbol;

  const defaultDisplayType: AmountFor = 'FIAT';

  const totalSum = getPrecisionFloatValue(totalAmount);

  const isBpf = isBpfsAsset(__typename as AssetType);

  const poweredByText = isBpf ? translations.poweredByBpfs : '';

  const taxWithholdingWarning = offerWarnings.includes(
    'tax.withholding.not.available',
  );

  const showWarningWithoutTaxes =
    offerWarnings.filter((item) => item !== 'tax.withholding.not.available')
      .length > 0;

  const shouldShowTaxSummary =
    (taxAmount && Number(taxAmount?.value) > 0 && fiatAmount?.value) ||
    taxWithholdingWarning;

  const triggerAcceptTradeOffer = async () => {
    setIsTradeOfferViewQueryRefetching(true);

    setOfferAccepted(true);

    let redirectPayload: IFinishTradeRedirectBuilderProps | undefined;

    try {
      const offerResponse = await acceptTradeOffer(offerId);

      if (offerResponse.errors) {
        setIsTradeOfferViewQueryRefetching(false);

        setOfferAccepted(false);

        processErrors(offerResponse.errors);

        // TODO: "error.mfa.verification.timeout" should be removed after releasing
        // all tenants to the consolidation in production.
        if (
          offerResponse.errors.includes('error.mfa.verification.timeout') ||
          offerResponse.errors.includes('trading.timeframe.elapsed')
        ) {
          router.replace(
            `${getAssetTradeUrl(asset.id, { type: variant })}?ref=portfolio`,
          );
        }

        return;
      }

      const statusResponse = offerResponse.data?.status;

      removeInputValueFromLocalStorage();

      redirectPayload = {
        assetId: asset.id,
        offerId,
        transactionStatus: statusResponse,
        variant,
        hasTaxOfflineWarning: !!hasTaxOfflineWarning,
      };
    } catch (error) {
      setIsTradeOfferViewQueryRefetching(false);

      setOfferAccepted(false);

      if (typeof error === 'string') {
        processErrors([error] as string[]);
      } else {
        processErrors(error as unknown as string[]);
      }

      datadogErrorHelper({
        errorMessage: JSON.stringify(error),
        errorSeverity: DatadogErrorLevels.CRITICAL,
      });

      return;
    }
    // Do not remove the loading state as after the operation is completed, the duration until the redirect is not known, and the user could submit multiple times
    // finally {
    //   setIsTradeOfferViewQueryRefetching(false);

    //   setOfferAccepted(false);
    // }

    if (redirectPayload) {
      try {
        await finishTradeRedirectBuilder(redirectPayload);
      } catch (redirectError) {
        if (isNextRedirectError(redirectError)) {
          throw redirectError;
        }

        setIsTradeOfferViewQueryRefetching(false);

        setOfferAccepted(false);

        processErrors([
          redirectError instanceof Error
            ? redirectError.message
            : String(redirectError),
        ]);

        datadogErrorHelper({
          errorMessage:
            redirectError instanceof Error
              ? redirectError.message
              : String(redirectError),
          errorSeverity: DatadogErrorLevels.CRITICAL,
        });

        return;
      }
    }
  };

  const maximumTradeAmount = getMaximumTradeAmount({
    fiatBalance: asset.portfolio?.fiatBalance,
    assetBalance: asset.portfolio?.assetBalance,
    type: variant,
  });

  const loaderElement = useMemo(
    // ? memoized to make sure the loader is not re-rendered before finishing due to isTradeOfferViewQueryRefetching state being changed;
    // ? -
    // ? isTradeOfferViewQueryRefetching is made true earlier than the offer expires to disable the confirmation button and make sure
    // ? the offer does not expire while being sent to the BE in the last milliseconds and slowed by latency;
    () =>
      nextExpiresAtInMs ? (
        <OfferLoaderElement
          key={refreshCount}
          duration={offerAvailabilityInMs}
          onComplete={handleSpinnerComplete}
          beforeCompleteCallbackMs={1000} // 1s
          beforeCompleteCallback={() =>
            setIsTradeOfferViewQueryRefetching(true)
          } // ? used for disabling the offer confirm button earlier than the offer expires; the amount of ms-before is specified by beforeCompleteCallbackMs
          onRefocus={() => handleSpinnerComplete()}
        />
      ) : null,
    [
      nextExpiresAtInMs,
      offerAvailabilityInMs,
      handleSpinnerComplete,
      refreshCount,
    ],
  );

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        px: 'medium',
        flex: '1',
      })}
    >
      <TradeAssetInfoDisplay
        sourceAssetData={asset as MutableNonNullableAsset}
        price={tradeOfferData.assetNetConversionRate}
        symbol={symbol as string}
        operation={variant}
        loaderElement={loaderElement}
      />

      <AmountInputWrapper
        inputContextData={{
          symbol: asset.symbol ?? '',
          currency,
          displayType: defaultDisplayType,
          value: totalSum,
          placeholder: undefined,
        }}
      />

      <div
        className={css({
          pt: 'extra_small',
          pb: 'medium',
        })}
      >
        <BelowInputInfoTable
          items={[
            {
              title: capitalizeFirstLetter(translations['units'], true),
              value: getConvertedSummary({
                assetPrice: tradeOfferData.assetConversionRate,
                tradeType: variant,
                assetType: tradeOfferData.asset.__typename as AssetType,
                currency: currency,
                displayType: defaultDisplayType,
                locale: currentLocale,
                symbol: symbol,
                translations,
                value: totalSum,
                useRawSource: {
                  rawSourceDisplayType: 'ASSET',
                  rawSourceValue: tradeOfferData.assetAmount as PrecisionFloat,
                },
                maximumTradeAmount,
              }),
            },
          ]}
        />
      </div>

      {showWarningWithoutTaxes ? (
        <AlertBox variant="warning-full">
          {offerWarnings.map((item) => item).join(', ')}
        </AlertBox>
      ) : null}
      <Divider />

      <FeesList
        showPriceGuarantee={translations.priceGuarantee}
        headerRightSlotContent={translations.feesIncluded}
        offerFeesDescription={translations.offerFeesDescription}
        items={getFeesListItemsForTrade(tradeOfferData, translations, {
          currency,
          currentLocale,
          isPaymentMethodVisible: feature_isPaymentMethodVisibleOnAccept,
          variant,
        })}
      />

      {feature_areTaxDetailsShownOnOfferAccept && shouldShowTaxSummary && (
        <TradeTaxSummary
          currency={currency}
          currentLocale={currentLocale}
          receivedAmount={fiatAmount}
          isTaxServiceOffline={!!hasTaxOfflineWarning || taxWithholdingWarning}
          taxAmount={taxAmount}
          translations={translations}
        />
      )}

      <SubmitButtonWrapper>
        <AcceptBuyTradeDisclaimerWrapper
          translations={translations}
          variant={variant}
          legalUrl={asset.legalUrl ?? ''}
          assetType={asset.__typename as AssetType}
        >
          {({ areTermsAgreedTo }) => (
            <>
              <PoweredBy
                value={isBpf ? poweredByText : translations.poweredByTenant}
                showLogo={!isBpf}
                noMargins
              />

              <div className={submitButtonWrapperAndButtonProperStyles}>
                <ButtonBase
                  visual="primary"
                  size="large"
                  disabled={
                    !areTermsAgreedTo || isTradeOfferViewQueryRefetching
                  }
                  onClick={triggerAcceptTradeOffer}
                  fullWidth
                >
                  {variant === 'buy'
                    ? translations.buyNow
                    : translations.sellNow}

                  {isTradeOfferViewQueryRefetching && (
                    <FilledSpinnerIos
                      aria-label="loading icon"
                      data-element="loading-icon"
                    />
                  )}
                </ButtonBase>
              </div>
            </>
          )}
        </AcceptBuyTradeDisclaimerWrapper>
      </SubmitButtonWrapper>
    </div>
  );
};
